import { useEffect } from "react";
import { OG_IMAGE, PAGES, SITE_URL } from "@/data/seo";

const setMeta = (attr, key, content) => {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const setLink = (rel, href, hreflang) => {
  const selector = hreflang
    ? `link[rel="${rel}"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]:not([hreflang])`;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    if (hreflang) el.setAttribute("hreflang", hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const setJsonLd = (id, data) => {
  let el = document.head.querySelector(`script[data-seo="${id}"]`);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.setAttribute("data-seo", id);
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
};

export const useSeo = ({ title, description, path = "/" } = {}) => {
  useEffect(() => {
    const page = PAGES[path] || {};
    const finalTitle = page.title || title;
    const finalDesc = page.description || description;
    const url = path === "/" ? `${SITE_URL}/` : `${SITE_URL}${path}`;

    document.title = finalTitle;
    setMeta("name", "description", finalDesc);
    if (page.keywords) setMeta("name", "keywords", page.keywords);
    setMeta("property", "og:title", finalTitle);
    setMeta("property", "og:description", finalDesc);
    setMeta("property", "og:url", url);
    setMeta("property", "og:image", OG_IMAGE);
    setMeta("name", "twitter:title", finalTitle);
    setMeta("name", "twitter:description", finalDesc);
    setMeta("name", "twitter:image", OG_IMAGE);

    setLink("canonical", url);
    setLink("alternate", url, "it-IT");

    setJsonLd("webpage", {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: finalTitle,
      description: finalDesc,
      url,
      inLanguage: "it-IT",
      isPartOf: {
        "@type": "WebSite",
        name: "DonnaImpresa",
        alternateName: ["Donna Impresa", "Donne Impresa", "Donne in Impresa"],
        url: `${SITE_URL}/`,
      },
    });
    setJsonLd("breadcrumb", {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
        ...(path === "/"
          ? []
          : [{ "@type": "ListItem", position: 2, name: page.h1 || finalTitle, item: url }]),
      ],
    });

    if (page.faq) {
      setJsonLd("faq-page", {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: page.faq.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      });
    }
    if (page.article) {
      setJsonLd("article", {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: page.h1 || finalTitle,
        description: finalDesc,
        inLanguage: "it-IT",
        image: OG_IMAGE,
        mainEntityOfPage: { "@type": "WebPage", "@id": url },
        author: { "@type": "Organization", name: "DonnaImpresa", url: `${SITE_URL}/` },
        publisher: {
          "@type": "Organization",
          name: "DonnaImpresa",
          alternateName: "Donna Impresa",
          url: `${SITE_URL}/`,
        },
        articleSection: (page.sezioni || []).map((s) => s.titolo),
      });
    }
  }, [title, description, path]);
};
